import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { ClientInstrument, EQUIPMENT_CATEGORY_OPTIONS } from './ServiceLinesEditor';
import { DocLangSelect, DocLangHint } from './DocLang';
import { usePermissions } from '../context/PermissionContext';

interface Client { id: number; company_name?: string; contact_person?: string; phone?: string; }
interface Product {
  id: number; name: string; name_ru?: string; name_en?: string;
  code: string; unit: string; sale_price: number; product_type?: string;
  manufacturer?: string; part_number?: string; model_number?: string; country_of_origin?: string;
}
interface QuotationItem {
  product_id?: number; product_name: string; product_name_ru?: string; product_name_en?: string; product_code: string;
  product_manufacturer?: string; part_number?: string; model_number?: string; country_of_origin?: string;
  unit: string; quantity: number; unit_price: number; discount: number; total_price: number;
  // xizmat qatori (faqat xizmat takliflari) - qarang ServiceLinesEditor
  instrument_id?: number | null; equipment_name?: string | null; equipment_serial?: string | null;
  equipment_category?: string | null; warranty?: string | null;
  warranty_value?: number | null; warranty_unit?: string | null;
}
interface Quotation {
  id: number; number: string; quotation_type: string; client_id: number; client?: Client;
  status: string; pipeline_stage: string; currency: string; valid_until?: string;
  notes?: string; total_amount: number; vat_percent: number; vat_amount: number; grand_total: number;
  net_amount: number; net_subtotal: number; net_discount_amount: number;
  delivery_days_min?: number; delivery_days_max?: number;
  execution_terms?: string; document_lang?: string | null; prepayment_percent?: number | null; prepayment_days?: number | null; final_payment_days?: number | null;
  created_by?: number;
  creator?: { id: number; full_name: string };
  assigned_to_id?: number; assignee?: { id: number; full_name: string };
  items: QuotationItem[]; created_at: string;
  locked_by_contract?: string | null;
}
interface AssignableUser { id: number; full_name: string; role: string; role_label?: string; }

const ROLE_GROUP_ORDER = ['admin','director','sales_head','sales_manager','service_head','service_engineer','warehouse_head','logistic_head','logist','chief_accountant','accountant','hr_manager','lawyer'];

const statusConfig: Record<string, { label: string; color: string }> = {
  draft:    { label: 'Qoralama',   color: 'bg-gray-100 text-gray-700' },
  sent:     { label: 'Yuborildi',  color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Qabul',      color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rad etildi', color: 'bg-red-100 text-red-700' },
};

const pipelineConfig: Record<string, { label: string; color: string; icon: string }> = {
  lead:                 { label: 'Lead',            color: 'bg-gray-100 text-gray-600',    icon: '🌱' },
  technical_discussion: { label: 'Texnik muhokama', color: 'bg-blue-100 text-blue-700',    icon: '🔧' },
  commercial_offer:     { label: 'Tijorat taklifi', color: 'bg-indigo-100 text-indigo-700',icon: '📄' },
  negotiation:          { label: 'Muzokara',         color: 'bg-yellow-100 text-yellow-700',icon: '🤝' },
  tender_contract:      { label: 'Tender/Shartnoma',color: 'bg-orange-100 text-orange-700',icon: '📝' },
  won:                  { label: 'Yutuq',            color: 'bg-green-100 text-green-700',  icon: '🏆' },
  lost:                 { label: "Yo'qotish",        color: 'bg-red-100 text-red-700',      icon: '❌' },
};

const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };
const unitLabels: Record<string, string> = { piece: 'Dona', box: 'Quti', pack: 'Paket', liter: 'Litr', kg: 'Kg', meter: 'Metr', hour: 'Soat', service_unit: 'Xizmat' };
const emptyItem: QuotationItem = { product_name: '', product_name_ru: '', product_name_en: '', product_code: '', product_manufacturer: '', part_number: '', model_number: '', country_of_origin: '', unit: '', quantity: 1, unit_price: 0, discount: 0, total_price: 0 };

interface QuotationListProps { quotationType: 'sales' | 'service'; title: string; icon: string; }

const QuotationList: React.FC<QuotationListProps> = ({ quotationType, title, icon }) => {
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const canFull = canEdit(quotationType === 'sales' ? 'sales_quotations' : 'service_quotations');
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Quotation | null>(null);
  const [showPdfModal, setShowPdfModal] = useState<Quotation | null>(null);
  const [pdfLang, setPdfLang] = useState<'uz' | 'ru' | 'en'>('uz');
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mahsulot qidirish
  const [productSearch, setProductSearch] = useState<Record<number, string>>({});
  const [showProductDropdown, setShowProductDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    client_id: '', currency: 'usd', valid_until: '', notes: '', pipeline_stage: 'lead', assigned_to_id: '', vat_percent: 0,
    delivery_days_min: '', delivery_days_max: '',
    execution_terms: '', prepayment_percent: '', prepayment_days: '', final_payment_days: '',
    document_lang: 'uz',
  });
  // Xizmat taklifi: shu taklif mijozining uskunalari (saqlangan taklif orqali, faqat o'qish)
  const [quotationInstruments, setQuotationInstruments] = useState<ClientInstrument[] | null>(null);
  const [savedClientId, setSavedClientId] = useState('');
  const [items, setItems] = useState<QuotationItem[]>([{ ...emptyItem }]);

  const relevantProductType = quotationType === 'sales' ? 'good' : 'service';
  const availableProducts = products.filter(p => (p.product_type || 'good') === relevantProductType);

  const fetchData = async () => {
    try { const q = await api.get('/quotations', { params: { quotation_type: quotationType } }); setQuotations(q.data); } catch {}
    try { const c = await api.get('/clients'); setClients(c.data); } catch { setClients([]); }
    try { const p = await api.get('/products'); setProducts(p.data); } catch { setProducts([]); }
    try { const u = await api.get('/users/assignable'); setAssignableUsers(u.data); } catch { setAssignableUsers([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [quotationType]);

  // Dashboard "So'nggi faoliyat" feed havolasi ?open=<id> bilan keladi -
  // shu ID'dagi kotirovkani to'g'ridan-to'g'ri detal oynasida ochadi
  // (ro'yxat sahifasiga tushib qolish o'rniga). Ruxsat bo'lmasa (403) -
  // jim o'tkazib yuboriladi, ro'yxat oddiy holda ko'rsatiladi.
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) {
      api.get(`/quotations/${openId}`).then(res => setShowDetail(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dropdown tashqarisiga click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowProductDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = quotations.filter(q => {
    const clientName = q.client?.company_name || q.client?.contact_person || '';
    const matchSearch = q.number.toLowerCase().includes(search.toLowerCase()) || clientName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? q.status === filterStatus : true;
    return matchSearch && matchStatus;
  });

  // Mahsulot qidiruv filtri
  const getFilteredProducts = (index: number) => {
    const s = (productSearch[index] || '').toLowerCase();
    if (!s) return availableProducts;
    return availableProducts.filter(p =>
      p.name.toLowerCase().includes(s) ||
      (p.name_ru || '').toLowerCase().includes(s) ||
      (p.name_en || '').toLowerCase().includes(s) ||
      p.code.toLowerCase().includes(s)
    );
  };

  const calcItemTotal = (item: QuotationItem) => {
    const price = item.unit_price * item.quantity;
    return Math.round((price - price * item.discount / 100) * 100) / 100;
  };
  // MUHIM BIZNES QOIDASI: narxlar QQS BILAN (gross) kiritiladi - QQS narxga
  // QO'SHILMAYDI, balki undan AJRATIB OLINADI. Backend bilan bir xil
  // formula/yaxlitlash (schemas/quotation.py, QuotationResponse) - "qoldiq"
  // (residual) usulida: net_amount avval hisoblanadi/yaxlitlanadi, vat_amount
  // esa AYIRISH orqali olinadi (mustaqil yaxlitlanmaydi) - shu bilan
  // net_amount + vat_amount har doim aniq totalAmount (gross)'ga teng bo'ladi.
  const subtotalAmount = Math.round(items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0) * 100) / 100;
  const totalAmount = items.reduce((sum, item) => sum + calcItemTotal(item), 0); // gross, chegirmadan keyin
  const vatPercentNum = Number(form.vat_percent) || 0;
  const netAmount = Math.round((totalAmount / (1 + vatPercentNum / 100)) * 100) / 100;
  const vatAmount = Math.round((totalAmount - netAmount) * 100) / 100; // qoldiq - net_amount'dan AYIRIB olinadi
  const grandTotal = Math.round(totalAmount * 100) / 100; // = gross, o'zgarishsiz - QQS allaqachon narxga kiritilgan
  const netSubtotal = Math.round((subtotalAmount / (1 + vatPercentNum / 100)) * 100) / 100;
  const netDiscountAmount = Math.round((netSubtotal - netAmount) * 100) / 100; // qoldiq

  const updateItem = (index: number, field: keyof QuotationItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (['quantity', 'unit_price', 'discount'].includes(field)) newItems[index].total_price = calcItemTotal(newItems[index]);
    setItems(newItems);
  };

  const selectProduct = (index: number, product: Product) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      product_name: product.name,
      product_name_ru: product.name_ru || '',
      product_name_en: product.name_en || '',
      product_code: product.code,
      product_manufacturer: product.manufacturer || '',
      part_number: product.part_number || '',
      model_number: product.model_number || '',
      country_of_origin: product.country_of_origin || '',
      unit: product.unit,
      unit_price: product.sale_price,
      total_price: calcItemTotal({ ...newItems[index], unit_price: product.sale_price }),
    };
    setItems(newItems);
    // Qidiruvni product nomiga o'rnat
    setProductSearch(prev => ({ ...prev, [index]: product.name }));
    setShowProductDropdown(null);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    setProductSearch(prev => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
  };

  const openAdd = () => {
    setForm({ client_id: '', currency: 'usd', valid_until: '', notes: '', pipeline_stage: 'lead', assigned_to_id: '', vat_percent: 0, delivery_days_min: '', delivery_days_max: '',
      execution_terms: '', prepayment_percent: '', prepayment_days: '', final_payment_days: '', document_lang: 'uz' });
    setQuotationInstruments(null); setSavedClientId('');
    setItems([{ ...emptyItem }]);
    setProductSearch({});
    setEditId(null); setError(''); setShowModal(true);
  };

  const openEdit = (q: Quotation) => {
    setForm({
      client_id: q.client_id.toString(), currency: q.currency, valid_until: q.valid_until ? q.valid_until.split('T')[0] : '', notes: q.notes || '', pipeline_stage: q.pipeline_stage || 'lead', assigned_to_id: q.assigned_to_id?.toString() || '', vat_percent: q.vat_percent || 0,
      delivery_days_min: q.delivery_days_min?.toString() || '', delivery_days_max: q.delivery_days_max?.toString() || '',
      execution_terms: q.execution_terms || '',
      document_lang: q.document_lang || 'uz',
      prepayment_percent: q.prepayment_percent?.toString() ?? '', prepayment_days: q.prepayment_days?.toString() ?? '',
      final_payment_days: q.final_payment_days?.toString() ?? '',
    });
    setSavedClientId(q.client_id.toString());
    setQuotationInstruments(null);
    if (quotationType === 'service') {
      api.get(`/quotations/${q.id}/client-instruments`).then(r => setQuotationInstruments(r.data)).catch(() => setQuotationInstruments(null));
    }
    const qItems = q.items.length > 0 ? q.items.map(i => ({ ...i })) : [{ ...emptyItem }];
    setItems(qItems);
    // Qidiruvni har bir item nomiga o'rnat
    const searches: Record<number, string> = {};
    qItems.forEach((item, idx) => { searches[idx] = item.product_name; });
    setProductSearch(searches);
    setEditId(q.id); setError(''); setShowModal(true);
  };

  // Bog'langan sotuv shartnomasi draft'dan chiqqan - qatorlar qulflangan (backend 409)
  const lockedBy = editId ? quotations.find(x => x.id === editId)?.locked_by_contract || null : null;

  const handleSubmit = async () => {
    if (!form.client_id) { setError("Mijoz tanlanishi kerak"); return; }
    // Sotuv: har bir mahsulot katalogdan. Xizmat: katalogdan yoki erkin matn
    // (Case'dan kelgan xizmatlar katalogsiz bo'lishi mumkin) - nom majburiy.
    const invalidItems = quotationType === 'sales' ? items.filter(i => !i.product_id) : items.filter(i => !i.product_name?.trim());
    if (invalidItems.length > 0) {
      setError(quotationType === 'sales' ? "Barcha mahsulotlar katalogdan tanlanishi shart!" : "Har bir xizmat qatorida nom bo'lishi kerak");
      return;
    }
    if (items.length === 0) { setError("Kamida bitta mahsulot qo'shing"); return; }
    const deliveryMin = form.delivery_days_min ? Number(form.delivery_days_min) : null;
    const deliveryMax = form.delivery_days_max ? Number(form.delivery_days_max) : null;
    if (deliveryMax !== null && deliveryMin !== null && deliveryMax < deliveryMin) {
      setError("Yetkazib berish muddati: \"gacha\" \"dan\"dan kichik bo'lishi mumkin emas"); return;
    }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form, quotation_type: quotationType, client_id: Number(form.client_id),
        assigned_to_id: form.assigned_to_id ? Number(form.assigned_to_id) : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        delivery_days_min: deliveryMin, delivery_days_max: deliveryMax,
        execution_terms: form.execution_terms || null,
        document_lang: quotationType === 'service' ? form.document_lang : null,
        prepayment_percent: form.prepayment_percent === '' ? null : Number(form.prepayment_percent),
        prepayment_days: form.prepayment_days === '' ? null : Number(form.prepayment_days),
        final_payment_days: form.final_payment_days === '' ? null : Number(form.final_payment_days),
        items: items.filter(i => i.product_name).map(i => ({
          ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price), discount: Number(i.discount),
          instrument_id: i.instrument_id || null, equipment_name: i.equipment_name || null,
          equipment_serial: i.equipment_serial || null, equipment_category: i.equipment_category || null,
          warranty: i.warranty_value ? null : (i.warranty || null),
          warranty_value: i.warranty_value ? Number(i.warranty_value) : null,
          warranty_unit: i.warranty_value ? (i.warranty_unit || 'month') : null,
        })),
      };
      if (editId) await api.put(`/quotations/${editId}`, payload);
      else await api.post('/quotations', payload);
      setShowModal(false); fetchData();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xatolik yuz berdi'); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    if (!canFull) return;
    await api.put(`/quotations/${id}`, { status });
    fetchData();
    if (showDetail) { const res = await api.get(`/quotations/${id}`); setShowDetail(res.data); }
  };

  const handlePipelineChange = async (id: number, pipeline_stage: string) => {
    if (!canFull) return;
    await api.put(`/quotations/${id}`, { pipeline_stage });
    fetchData();
    if (showDetail) { const res = await api.get(`/quotations/${id}`); setShowDetail(res.data); }
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/quotations/${id}`);
    setShowDetail(null); fetchData();
  };

  const handleExport = async (id: number, number: string) => {
    const res = await api.get(`/quotations/${id}/export`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `${number}.xlsx`; a.click();
  };

  const handleExportPdf = async (q: Quotation) => {
    // hujjat tili tanlangan bo'lsa - PDF shu tilda (o'zgartirish mumkin)
    if (q.document_lang === 'uz' || q.document_lang === 'ru' || q.document_lang === 'en') setPdfLang(q.document_lang);
    setShowPdfModal(q);
  };

  const doExportPdf = async () => {
    if (!showPdfModal) return;
    try {
      const res = await api.get(`/quotations/${showPdfModal.id}/export/pdf`, {
        params: { lang: pdfLang },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${showPdfModal.number}_${pdfLang}.pdf`; a.click();
      setShowPdfModal(null);
    } catch { alert('PDF yaratishda xato'); }
  };

  const handleCreateContract = async (id: number, number: string) => {
    if (!window.confirm(`${number} asosida shartnoma yaratilasinmi?`)) return;
    try {
      const res = await api.post(`/contracts/from-quotation/${id}`);
      const cNumber = res.data.number;
      if (window.confirm(`✅ Shartnoma yaratildi: ${cNumber}\n\nBuxgalteriya sahifasiga o'tish uchun OK bosing.`)) {
        window.location.href = '/accounting';
      }
    } catch (e: any) { alert(e.response?.data?.detail || 'Xato yuz berdi'); }
  };

  const getClientName = (client?: Client) => client?.company_name || client?.contact_person || '—';

  const langLabels: Record<string, string> = { uz: "🇺🇿 O'zbek", ru: '🇷🇺 Rus', en: '🇬🇧 Ingliz' };

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">{icon} {title}</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + <span className="hidden sm:inline">Yangi {title.toLowerCase()}</span>
        </button>
      </div>

      {/* Status kartalar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        {Object.entries(statusConfig).map(([key, val]) => (
          <div key={key} className="bg-white rounded-xl shadow p-3 md:p-4">
            <p className="text-gray-500 text-xs md:text-sm">{val.label}</p>
            <p className="text-xl md:text-2xl font-bold text-gray-800">{quotations.filter(q => q.status === key).length}</p>
          </div>
        ))}
      </div>

      {/* Filtrlar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input type="text" placeholder="🔍 Raqam yoki mijoz..." value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha holatlar</option>
          {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Jadval */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 py-3 text-left">Raqam / Mijoz</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Pipeline</th>
              <th className="px-3 py-3 text-left">Holat</th>
              <th className="px-3 py-3 text-right hidden sm:table-cell">Summa</th>
              <th className="px-3 py-3 text-left hidden lg:table-cell">Mas'ul</th>
              <th className="px-3 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">Takliflar yo'q</td></tr>
            ) : filtered.map(q => (
              <tr key={q.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-3">
                  <div className="font-mono text-blue-600 font-medium text-xs">{q.number}</div>
                  <div className="font-medium text-gray-800 text-sm">{getClientName(q.client)}</div>
                  <div className="sm:hidden text-xs font-bold text-blue-600 mt-0.5">
                    {q.total_amount.toLocaleString()} {currencySymbol[q.currency]}
                  </div>
                </td>
                <td className="px-3 py-3 hidden md:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${pipelineConfig[q.pipeline_stage]?.color}`}>
                    {pipelineConfig[q.pipeline_stage]?.icon} {pipelineConfig[q.pipeline_stage]?.label}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig[q.status]?.color}`}>
                    {statusConfig[q.status]?.label}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap hidden sm:table-cell">
                  {q.total_amount.toLocaleString()} {currencySymbol[q.currency]}
                </td>
                <td className="px-3 py-3 text-gray-600 text-xs hidden lg:table-cell">{q.assignee?.full_name || '—'}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(q)} className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => handleExport(q.id, q.number)} className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs">⬇️</button>
                    <button onClick={() => handleExportPdf(q)} className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">📄</button>
                    <button onClick={() => canFull && handleCreateContract(q.id, q.number)} disabled={!canFull}
                      className={`p-1.5 rounded text-xs ${canFull ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>📝</button>
                    <button onClick={() => canFull && openEdit(q)} disabled={!canFull}
                      className={`p-1.5 rounded text-xs ${canFull ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>✏️</button>
                    <button onClick={() => handleDelete(q.id)} disabled={!canFull}
                      className={`p-1.5 rounded text-xs ${canFull ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDF Til tanlash Modal — boshqa modallar (masalan Detail) ustida ochilishi mumkin, shuning uchun yuqoriroq z-index */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-2">📄 PDF tili</h3>
            <p className="text-gray-500 text-sm mb-4">{showPdfModal.number} — tanlangan tilda tovar nomlari ham ko'rsatiladi</p>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {(['uz', 'ru', 'en'] as const).map(lang => (
                <button key={lang} onClick={() => setPdfLang(lang)}
                  className={`py-3 rounded-xl border-2 text-sm font-medium transition ${pdfLang === lang ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  {langLabels[lang]}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowPdfModal(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={doExportPdf} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">📥 Yuklab olish</button>
            </div>
          </div>
        </div>
      )}

      {/* Yaratish/Tahrirlash Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-4 md:p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Tahrirlash' : `Yangi ${title.toLowerCase()}`}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            {lockedBy && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg mb-4 text-xs">
                🔒 Bu taklif <b>{lockedBy}</b> sotuv shartnomasiga bog&apos;langan va shartnoma qoralama emas. Shartnoma spetsifikatsiyasi
                shu taklif qatorlaridan olinadi, shuning uchun qatorlar, QQS, valyuta va mijozni o&apos;zgartirib bo&apos;lmaydi
                (imzolangan shartnoma bilan tizimdagi ma&apos;lumot farq qilmasligi uchun). Boshqa maydonlar (bosqich, izoh, mas&apos;ul) tahrirlanadi.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-5">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz *</label>
                <select value={form.client_id} disabled={!!lockedBy} onChange={e => setForm({ ...form, client_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Mijozni tanlang —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={form.currency} disabled={!!lockedBy} onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD — Dollar</option>
                  <option value="eur">EUR — Euro</option>
                  <option value="uzs">UZS — So'm</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amal qilish muddati</label>
                <input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">QQS (%)</label>
                <input type="number" min="0" max="100" value={form.vat_percent} disabled={!!lockedBy}
                  onChange={e => setForm({ ...form, vat_percent: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {quotationType === 'service' && (
                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <DocLangSelect value={form.document_lang} onChange={v => setForm({ ...form, document_lang: v })} />
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bajarish muddati va tartibi</label>
                    <textarea value={form.execution_terms} onChange={e => setForm({ ...form, execution_terms: e.target.value })}
                      rows={2} placeholder="masalan: ariza olingandan keyin 5 ish kuni ichida, buyurtmachi hududida"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <DocLangHint lang={form.document_lang} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Oldindan to&apos;lov (%)</label>
                    <input type="number" min="0" max="100" placeholder="50" value={form.prepayment_percent}
                      onChange={e => setForm({ ...form, prepayment_percent: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Oldindan to&apos;lov muddati (bank kuni)</label>
                    <input type="number" min="0" placeholder="3" value={form.prepayment_days}
                      onChange={e => setForm({ ...form, prepayment_days: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Yakuniy to&apos;lov (ish kuni, dalolatnomadan)</label>
                    <input type="number" min="0" placeholder="5" value={form.final_payment_days}
                      onChange={e => setForm({ ...form, final_payment_days: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <p className="text-[11px] text-gray-400 self-end">Bo&apos;sh qoldirilsa standart shartlar. Shartnomaga avtomatik o&apos;tadi.</p>
                </div>
              )}
              {quotationType === 'sales' && <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib berish muddati (kun)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input type="number" min="0" placeholder="dan" value={form.delivery_days_min}
                    onChange={e => setForm({ ...form, delivery_days_min: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="number" min="0" placeholder="gacha (ixtiyoriy)" value={form.delivery_days_max}
                    onChange={e => setForm({ ...form, delivery_days_max: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline bosqichi</label>
                <select value={form.pipeline_stage} onChange={e => setForm({ ...form, pipeline_stage: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(pipelineConfig).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul hodim</label>
                <select value={form.assigned_to_id} onChange={e => setForm({ ...form, assigned_to_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {Array.from(new Set(assignableUsers.map(u => u.role))).sort((a, b) => { const ia = ROLE_GROUP_ORDER.indexOf(a); const ib = ROLE_GROUP_ORDER.indexOf(b); return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib); }).map(role => {
                    const groupUsers = assignableUsers.filter(u => u.role === role);
                    return <optgroup key={role} label={groupUsers[0]?.role_label || role}>{groupUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</optgroup>;
                  })}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* Mahsulotlar */}
            <div className={`mb-4 ${lockedBy ? 'pointer-events-none opacity-60' : ''}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700 text-sm">{quotationType === 'sales' ? 'Mahsulotlar' : 'Xizmatlar'}</h4>
                <button onClick={addItem} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm">+ Qo'shish</button>
              </div>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-400 text-xs font-medium w-5">{index + 1}</span>
                      {/* Mahsulot qidirish */}
                      <div className="flex-1 relative" ref={showProductDropdown === index ? dropdownRef : null}>
                        <input
                          value={productSearch[index] || ''}
                          onChange={e => {
                            setProductSearch(prev => ({ ...prev, [index]: e.target.value }));
                            setShowProductDropdown(index);
                            // Xizmat: katalogdan tanlanmasa ham yozilgan matn - qator nomi (erkin matn)
                            if (quotationType === 'service') {
                              const v = e.target.value;
                              setItems(prev => prev.map((it, i) => i === index ? { ...it, product_name: v, product_id: undefined, product_code: '' } : it));
                            }
                          }}
                          onFocus={() => setShowProductDropdown(index)}
                          placeholder={`🔍 ${quotationType === 'sales' ? 'Mahsulot' : 'Xizmat'} qidiring...`}
                          className={`w-full border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${!item.product_id ? 'border-orange-300 bg-orange-50' : 'border-green-300 bg-green-50'}`}
                        />
                        {showProductDropdown === index && (
                          <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
                            {getFilteredProducts(index).length === 0 ? (
                              <div className="px-3 py-2 text-xs text-gray-400">Topilmadi</div>
                            ) : getFilteredProducts(index).map(p => (
                              <button key={p.id} onClick={() => selectProduct(index, p)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b last:border-0">
                                <div className="font-medium text-gray-800">{p.name}</div>
                                {p.name_ru && <div className="text-gray-400">{p.name_ru}</div>}
                                <div className="text-blue-500 font-mono">{p.code}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {items.length > 1 && (
                        <button onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                      )}
                    </div>
                    {item.product_id && (
                      <div className="text-xs text-green-600 mb-2 pl-7">✅ {item.product_code}</div>
                    )}
                    {(item.product_manufacturer || item.model_number || item.country_of_origin) && (
                      <div className="text-xs text-gray-400 mb-2 pl-7 truncate">
                        {item.product_manufacturer && `Brend: ${item.product_manufacturer}`}
                        {item.model_number && ` · Model: ${item.model_number}`}
                        {item.country_of_origin && ` · ${item.country_of_origin}`}
                      </div>
                    )}
                    {quotationType === 'service' && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 pl-7 mb-2">
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-500 mb-0.5">Uskuna</label>
                          {quotationInstruments !== null && editId && form.client_id === savedClientId && (
                            <select value={item.instrument_id || ''} onChange={e => {
                              const ins = quotationInstruments.find(i => i.id === Number(e.target.value));
                              setItems(prev => prev.map((it, i) => i === index ? { ...it, instrument_id: ins ? ins.id : null,
                                equipment_name: ins ? ins.label : it.equipment_name, equipment_serial: ins ? (ins.serial_number || '') : it.equipment_serial } : it));
                            }} className="w-full border rounded px-2 py-1 text-xs mb-1">
                              <option value="">— erkin matn —</option>
                              {quotationInstruments.map(i => <option key={i.id} value={i.id}>{i.label}{i.serial_number ? ` (${i.serial_number})` : ''}</option>)}
                            </select>
                          )}
                          <div className="grid grid-cols-2 gap-1">
                            <input value={item.equipment_name || ''} disabled={!!item.instrument_id} placeholder="Uskuna nomi"
                              onChange={e => updateItem(index, 'equipment_name', e.target.value)} className="w-full border rounded px-2 py-1 text-xs" />
                            <input value={item.equipment_serial || ''} disabled={!!item.instrument_id} placeholder="Seriya raqami"
                              onChange={e => updateItem(index, 'equipment_serial', e.target.value)} className="w-full border rounded px-2 py-1 text-xs" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Uskuna toifasi</label>
                          <select value={item.equipment_category || ''} onChange={e => updateItem(index, 'equipment_category', e.target.value)}
                            className="w-full border rounded px-2 py-1 text-xs">
                            <option value="">—</option>
                            {Object.entries(EQUIPMENT_CATEGORY_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Kafolat (ixtiyoriy)</label>
                          <div className="flex gap-1">
                <input type="number" min={1} max={600} value={item.warranty_value ?? ''}
                  onChange={e => (v => updateItem(index, 'warranty_value', v))(e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="son" className={'w-20 border rounded px-2 py-1 text-xs'} />
                <select value={item.warranty_unit || 'month'} onChange={e => (v => updateItem(index, 'warranty_unit', v))(e.target.value)} className='border rounded px-2 py-1 text-xs'>
                  <option value="month">oy</option><option value="day">kun</option>
                </select>
              </div>
                          {item.warranty && !item.warranty_value && <p className="text-[11px] text-orange-600">Eski matn: «{item.warranty}»</p>}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pl-7">
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Birlik</label>
                        <input value={unitLabels[item.unit] || item.unit} onChange={e => updateItem(index, 'unit', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Miqdor</label>
                        <input type="number" min="0" value={item.quantity} onChange={e => updateItem(index, 'quantity', Number(e.target.value))}
                          className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Narx</label>
                        <input type="number" min="0" value={item.unit_price} onChange={e => updateItem(index, 'unit_price', Number(e.target.value))}
                          className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Chegirma%</label>
                        <input type="number" min="0" max="100" value={item.discount} onChange={e => updateItem(index, 'discount', Number(e.target.value))}
                          className="w-full border rounded px-2 py-1 text-xs text-center focus:outline-none" />
                      </div>
                    </div>
                    <div className="text-right mt-2 pr-1">
                      <span className="text-xs text-gray-500">Jami: </span>
                      <span className="font-bold text-green-600 text-sm">{calcItemTotal(item).toLocaleString()} {currencySymbol[form.currency]}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 ml-auto max-w-xs text-sm space-y-1">
                <div className="flex justify-between text-gray-500">
                  <span>Oraliq jami (QQS siz)</span><span>{netSubtotal.toLocaleString()} {currencySymbol[form.currency]}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Chegirma (QQS siz)</span><span>-{netDiscountAmount.toLocaleString()} {currencySymbol[form.currency]}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>QQS ({form.vat_percent}%)</span><span>{vatAmount.toLocaleString()} {currencySymbol[form.currency]}</span>
                </div>
                <div className="flex justify-between font-bold text-blue-600 border-t pt-1">
                  <span>JAMI (QQS bilan)</span><span>{grandTotal.toLocaleString()} {currencySymbol[form.currency]}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg md:text-xl font-bold font-mono">{showDetail.number}</h3>
                <p className="text-gray-500 text-sm">{getClientName(showDetail.client)}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[showDetail.status]?.color}`}>
                {statusConfig[showDetail.status]?.label}
              </span>
            </div>

            <div className="flex gap-2 mb-4 flex-wrap">
              {Object.entries(statusConfig).map(([key, val]) => (
                <button key={key} onClick={() => handleStatusChange(showDetail.id, key)} disabled={showDetail.status === key || !canFull}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${showDetail.status === key || !canFull ? 'opacity-50 cursor-not-allowed border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>
                  {val.label}
                </button>
              ))}
            </div>

            {showDetail.delivery_days_min != null && (
              <p className="text-xs text-gray-500 mb-4">
                🚚 Yetkazib berish muddati: <span className="font-medium text-gray-700">
                  {showDetail.delivery_days_max && showDetail.delivery_days_max > showDetail.delivery_days_min
                    ? `${showDetail.delivery_days_min}-${showDetail.delivery_days_max}`
                    : showDetail.delivery_days_min} kun
                </span>
              </p>
            )}

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Pipeline bosqichi</p>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(pipelineConfig).map(([key, val]) => (
                  <button key={key} onClick={() => handlePipelineChange(showDetail.id, key)} disabled={showDetail.pipeline_stage === key || !canFull}
                    className={`px-2 py-1 rounded-lg text-xs font-medium border transition ${showDetail.pipeline_stage === key ? val.color + ' border-transparent' : !canFull ? 'opacity-50 cursor-not-allowed border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>
                    {val.icon} {val.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="px-3 py-2 text-left">{quotationType === 'sales' ? 'Mahsulot' : 'Xizmat'}</th>
                    <th className="px-3 py-2 text-center hidden sm:table-cell">Miqdor</th>
                    <th className="px-3 py-2 text-center hidden sm:table-cell">Narx (QQS bilan)</th>
                    <th className="px-3 py-2 text-center hidden md:table-cell">Chegirma</th>
                    <th className="px-3 py-2 text-right">Jami (QQS bilan)</th>
                  </tr>
                </thead>
                <tbody>
                  {showDetail.items.map((item, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2">
                        <div className="font-medium text-sm">{item.product_name}</div>
                        {item.product_code && <div className="text-xs text-gray-400">{item.product_code}</div>}
                        <div className="sm:hidden text-xs text-gray-500 mt-0.5">{item.quantity} {unitLabels[item.unit] || item.unit} × {item.unit_price.toLocaleString()}</div>
                      </td>
                      <td className="px-3 py-2 text-center text-xs hidden sm:table-cell">{item.quantity} {unitLabels[item.unit] || item.unit}</td>
                      <td className="px-3 py-2 text-center text-xs hidden sm:table-cell">{item.unit_price.toLocaleString()}</td>
                      <td className="px-3 py-2 text-center text-xs hidden md:table-cell">{item.discount}%</td>
                      <td className="px-3 py-2 text-right font-medium text-xs">{item.total_price.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2">
                    <td colSpan={4} className="px-3 py-2 text-right text-xs text-gray-500">Oraliq jami (QQS siz):</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">{showDetail.net_subtotal.toLocaleString()} {currencySymbol[showDetail.currency]}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right text-xs text-gray-500">Chegirma (QQS siz):</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">-{showDetail.net_discount_amount.toLocaleString()} {currencySymbol[showDetail.currency]}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right text-xs text-gray-500">QQS ({showDetail.vat_percent}%):</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">{showDetail.vat_amount.toLocaleString()} {currencySymbol[showDetail.currency]}</td>
                  </tr>
                  <tr className="border-t">
                    <td colSpan={4} className="px-3 py-2 text-right font-bold text-sm">JAMI (QQS bilan):</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-600">{showDetail.grand_total.toLocaleString()} {currencySymbol[showDetail.currency]}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {showDetail.notes && <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-600"><b>Izoh:</b> {showDetail.notes}</div>}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-4">
              {showDetail.assignee && <span>👤 <b className="text-gray-700">Mas'ul:</b> {showDetail.assignee.full_name}</span>}
              {showDetail.creator && <span>✍️ <b className="text-gray-700">Yaratuvchi:</b> {showDetail.creator.full_name}</span>}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <button onClick={() => handleExport(showDetail.id, showDetail.number)} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">⬇️ Excel</button>
              <button onClick={() => handleExportPdf(showDetail)} className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">📄 PDF</button>
              <button onClick={() => canFull && handleCreateContract(showDetail.id, showDetail.number)} disabled={!canFull}
                className={`px-3 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'}`}>📝 Shartnoma</button>
              <button onClick={() => navigate('/accounting?tab=supply')} className="px-3 py-2 rounded-lg text-sm bg-purple-100 text-purple-700 hover:bg-purple-200">📋 Shartnomalarni ko'rish</button>
              <button onClick={() => { if (canFull) { setShowDetail(null); openEdit(showDetail); } }} disabled={!canFull}
                className={`px-3 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>✏️ Tahrirlash</button>
              <button onClick={() => setShowDetail(null)} className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationList;
